import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { NotificationType } from 'angular2-notifications'
import { of } from 'rxjs'
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { $app } from '../../../app.service'
import { AppNotificationService } from '../../services/app-notification.service'
import { ProjectService } from '../project/project.service'
import { StageService } from './stage.service'
import { AppBasicResultsPaginated } from '../../models/interfaces/app-basic-results-paginated.interface'
import { AppOrganization, AppOrganizationRequest } from '../../models/interfaces/app-organization.interface'
import { AppProject } from '../../models/interfaces/app-project.interface'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { AppSpinnerService } from '../../services/app-spinner.service'
import { AppState } from '../storage'
import * as StageActions from './stage.actions'
import { selectIsSidebarOpen } from './stage.selectors'

@Injectable()
export class StageEffects {
   loadCountries$ = createEffect(() =>
      this.actions$.pipe(
         ofType(StageActions.loadCountries),
         switchMap(() =>
            this.stageService.getCountries().pipe(
               map((countries) => StageActions.loadCountriesSuccess({ response: countries })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.loadCountriesFailed({ error: error }))
               })
            )
         )
      )
   )

   loadStatutes$ = createEffect(() =>
      this.actions$.pipe(
         ofType(StageActions.setStatuses),
         switchMap(() =>
            this.stageService.getStatuses().pipe(
               map((statuses) => StageActions.setStatusesSuccess({ statuses })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setStatusesFailure({ error: error }))
               })
            )
         )
      )
   )

   setProject$ = createEffect(() =>
      this.actions$.pipe(
         ofType(StageActions.setProject),
         switchMap((action) =>
            this._projectService.getProjectById(action.projectId).pipe(
               map((project: AppProject) => {
                  $app.core.projectStatus = project.statusId
                  return StageActions.setProjectSuccess({ project })
               }),
               catchError((error) => of(StageActions.setProjectError({ error })))
            )
         )
      )
   )

   searchCRMOrganizationList$ = createEffect(() =>
      this.actions$.pipe(
         ofType(StageActions.searchCRMOrganizationList),
         map((action) => action.query),
         switchMap((query: string) =>
            this.stageService.searchOrganizationsList(query).pipe(
               map((response: AppBasicResultsPaginated<AppOrganizationRequest, AppOrganization>) =>
                  StageActions.searchCRMOrganizationListSuccess({ response: response.results })
               ),
               catchError((error) => of(StageActions.searchCRMOrganizationListFailure({ error })))
            )
         )
      )
   )

   toggleMenu$ = createEffect(() =>
      this.actions$.pipe(
         ofType(StageActions.toggleSidebar),
         withLatestFrom(this.store.pipe(select(selectIsSidebarOpen))),
         switchMap(([action, isMenuOpen]) => {
            return of(isMenuOpen ? StageActions.closeSideBar() : StageActions.openSidebar())
         })
      )
   )

   constructor(
      private router: Router,
      private actions$: Actions,
      private _projectService: ProjectService,
      private store: Store<AppState>,
      private stageService: StageService,
      private spinnerService: AppSpinnerService,
      private notificationService: AppNotificationService,
      private translate: TranslateService,
      private errorHandlerService: AppErrorHandlerService
   ) {}

   private _actionsFailedNotification = [StageActions.loadCountriesFailed, StageActions.setStatusesFailure, StageActions.searchCRMOrganizationListFailure]

   listenAllFailedAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsFailedNotification),
            map((errorResponse) => this.errorHandlerService.handleError(errorResponse.error))
         ),
      { dispatch: false }
   )

   listenSetProjectError$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(StageActions.setProjectError),
            map(() => {
               this.notificationService.notify(
                  NotificationType.Error,
                  this.translate.instant('error_project_not_found_title'),
                  this.translate.instant('error_project_not_found_message')
               )
               this.spinnerService.startupHide()
               this.router.navigate(['/error'])
            })
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerHide = [StageActions.setProjectSuccess]

   listenAllSpinnerHideAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerHide),
            map(() => this.spinnerService.spinnerHide())
         ),
      { dispatch: false }
   )

   private _actionsForSpinnerShow = [StageActions.setProject]

   listenAllSpinnerShowAction$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(...this._actionsForSpinnerShow),
            map(() => this.spinnerService.spinnerShow())
         ),
      { dispatch: false }
   )
}
